/**
 * DG Member — single client-side accessor for Drummond Club entitlement.
 *
 * Reads the server-rendered window.__dcMemberContext (set by
 * snippets/DG-member-context.liquid) and exposes:
 *
 *   window.dcMember.isEntitled()        → boolean
 *   window.dcMember.getPrice(variantId) → { value: number, asMoney: string } | null
 *
 * Fires a dcMember:ready CustomEvent on document when the API is ready.
 * Fail-closed: if context data is absent, isEntitled() returns false.
 *
 * DG-member-context.liquid creates a synchronous stub with isEntitled() so
 * early-init surfaces (e.g. Rebuy) can call it before this deferred script
 * runs. This file augments that stub with getPrice() and fires dcMember:ready.
 *
 * RULE: Any new client-side pricing surface (Rebuy templates, upsell widgets,
 * third-party apps, etc.) must consume this API instead of deriving entitlement
 * independently. See docs/member-pricing.md for the full contract.
 */
(function () {
  'use strict';

  var ctx = window.__dcMemberContext || {
    isEntitled: false,
    hasMemberTag: false,
    hasMembershipInCart: false,
    customerId: null
  };

  // Preserve the synchronous stub from DG-member-context.liquid (if present)
  // and augment it with getPrice(), which requires DOM access.
  window.dcMember = window.dcMember || {};

  window.dcMember.isEntitled = window.dcMember.isEntitled || function () {
    return ctx.isEntitled === true;
  };

  /**
   * Returns the member price for a variant from the current page's product
   * form data (data-club-member-pricing-variants element), or null when:
   *   - the customer is not entitled
   *   - no form data element exists on the page
   *   - the specific variant has no member price set
   *
   * Primarily used by PDP-level JS. For Rebuy cart line items, read
   * item.properties._club_member_price from the Rebuy template context
   * instead (the value is stored on the line item at add-to-cart time).
   *
   * @param {number} variantId
   * @returns {{ value: number, asMoney: string } | null}
   */
  window.dcMember.getPrice = function (variantId) {
    if (!this.isEntitled()) return null;

    var dataEl = document.querySelector('[data-club-member-pricing-variants]');
    if (!dataEl) return null;

    try {
      var data = JSON.parse(dataEl.textContent);
      var variant = data.variants.find(function (v) {
        return v.id === variantId;
      });
      if (!variant || variant.club_member_price.value === null) return null;
      return variant.club_member_price;
    } catch (e) {
      return null;
    }
  };

  document.dispatchEvent(new CustomEvent('dcMember:ready', { detail: window.dcMember }));
})();
